import { useCallback, useEffect, useState } from "react";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { eventTypes } from "../utils/toolbar";
import {
  FORMAT_TEXT_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  UNDO_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  $getSelection,
  $isRangeSelection,
  INDENT_CONTENT_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from "lexical";
import {
  $isListNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  ListNode,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $getNearestNodeOfType, mergeRegister } from "@lexical/utils";
import { $isAtNodeEnd } from "@lexical/selection";
import { OPTIONS } from "../utils/core";
import { $patchStyledNodes } from "../utils/common";
import {
  dispatchSignatureStyle,
  useIsSignatureActive,
} from "../plugins/PasterPlugin/ToolbarBridge";
import { FORMAT_TO_EXEC } from "../plugins/PasterPlugin/HtmlDecorator";

const LowPriority = 1;

// Helper function to convert RGB to Hex
function rgbToHex(rgb: string): string {
  if (!rgb) return rgb;

  // If already hex, return as is
  if (rgb.startsWith("#")) return rgb;

  // If rgba with transparency, return as is (can't convert to simple hex)
  if (rgb.startsWith("rgba")) {
    const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (match) {
      const [, r, g, b, a] = match;
      // If fully transparent, return transparent
      if (a === "0") return "transparent";
      // Convert to hex (ignoring alpha for now)
      return (
        "#" +
        [r, g, b]
          .map((x) => {
            const hex = parseInt(x).toString(16);
            return hex.length === 1 ? "0" + hex : hex;
          })
          .join("")
      );
    }
  }

  // Handle rgb format
  const match = rgb.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (match) {
    const [, r, g, b] = match;
    return (
      "#" +
      [r, g, b]
        .map((x) => {
          const hex = parseInt(x).toString(16);
          return hex.length === 1 ? "0" + hex : hex;
        })
        .join("")
    );
  }

  return rgb;
}

function useOnClickListener() {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState("paragraph");
  const [selectedEventTypes, setSelectedEventTypes] = useState<any>({
    [eventTypes.fontFamily]: "sans-serif",
    [eventTypes.fontSize]: 16,
    [eventTypes.align.base]: "left",
    [eventTypes.bold]: false,
    [eventTypes.italic]: false,
    [eventTypes.underline]: false,
  });
  const [isLink, setIsLink] = useState(false);

  const isSignatureActive = useIsSignatureActive();

  const updateToolbar = useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      const newState: any = {
        // Set default values
        [eventTypes.fontFamily]: "sans-serif",
        [eventTypes.fontSize]: 16,
        [eventTypes.align.base]: "left",
      };

      // range selection ( e.g like to bold only the particular area of the text)
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        if (anchorNode === null) return;
        const element =
          anchorNode.getKey() === "root"
            ? anchorNode
            : anchorNode.getTopLevelElementOrThrow();

        const elementKey = element.getKey();
        const elementDOM = editor.getElementByKey(elementKey);

        if (elementDOM !== null) {
          if ($isListNode(element)) {
            const parentList = $getNearestNodeOfType(anchorNode, ListNode);
            const type = parentList ? parentList.getTag() : element.getTag();
            setBlockType(type);
          } else {
            const type = element.getType();
            setBlockType(type);
          }
        }

        // Check formatting and add to selected events
        newState[eventTypes.strike] = selection.hasFormat("strikethrough");
        newState[eventTypes.code] = selection.hasFormat("code");

        // Get inline styles from selection
        const node = getSelectedNode(selection);
        const parent = node.getParent();

        // Check for link
        if ($isLinkNode(parent) || $isLinkNode(node)) {
          newState[eventTypes.link] = true;
          setIsLink(true);
        } else {
          newState[eventTypes.link] = false;
          setIsLink(false);
        }

        // Get the actual text node's DOM element for accurate style reading
        const textNodeDOM = editor.getElementByKey(node.getKey());
        const parentElement = editor.getElementByKey(parent.getKey());

        // Try multiple elements to find inline styles (Lexical wraps text in spans)
        const styleElement = textNodeDOM || parentElement || elementDOM;

        // Get computed styles from the node
        if (styleElement) {
          const computedStyle = window.getComputedStyle(styleElement);

          // Get font family - extract first font from the list
          let fontFamily = "";

          // If selection is collapsed, check for selection style overrides
          if (selection.isCollapsed()) {
            fontFamily =
              selection.style
                .split("font-family:")[1]
                ?.split(";")[0]
                ?.trim()
                .replace(/['"]/g, "") || "";
          }

          if (!fontFamily) {
            fontFamily = computedStyle.getPropertyValue("font-family");
          }

          if (fontFamily) {
            let firstFont = fontFamily
              .split(",")[0]
              .trim()
              .replace(/['"]/g, "");
            // Normalize system fonts to sans-serif
            if (
              [
                "-apple-system",
                "BlinkMacSystemFont",
                "system-ui",
                "Arial",
                "Helvetica",
              ].includes(firstFont)
            ) {
              firstFont = "sans-serif";
            } else if (["Times New Roman", "Times"].includes(firstFont)) {
              firstFont = "sans-serif";
            }
            newState[eventTypes.fontFamily] = firstFont;
          }

          // Get font size - extract only the number
          let fontSize = "";
          if (selection.isCollapsed()) {
            fontSize =
              selection.style.split("font-size:")[1]?.split(";")[0]?.trim() ||
              "";
          }

          if (!fontSize) {
            fontSize = computedStyle.getPropertyValue("font-size");
          }

          if (fontSize) {
            const fontSizeNum = parseInt(fontSize, 10);
            newState[eventTypes.fontSize] = fontSizeNum || 16; // Default to 16 if parsing fails
          }

          // Get text color and background color
          // First try to get inline styles from the element itself
          let color = null;
          let backgroundColor = null;

          if (styleElement instanceof HTMLElement && styleElement.style) {
            // Check inline styles first (more accurate for Lexical's inline formatting)
            color = styleElement.style.color || null;
            backgroundColor = styleElement.style.backgroundColor || null;
          }

          // Only set if we have actual colors (not transparent/initial)
          const hexColor = color ? rgbToHex(color) : null;
          const hexBgColor =
            backgroundColor &&
            backgroundColor !== "rgba(0, 0, 0, 0)" &&
            backgroundColor !== "transparent"
              ? rgbToHex(backgroundColor)
              : null;

          if (hexColor || hexBgColor) {
            newState[eventTypes.color.base] = {
              text: hexColor || null,
              background: hexBgColor || null,
            };
          } else {
            newState[eventTypes.color.base] = {
              text: null,
              background: null,
            };
          }

          // Get text alignment directly from the Lexical block element
          let textAlign = typeof (element as any).getFormatType === 'function' 
            ? (element as any).getFormatType() 
            : "left";
          if (textAlign === "start" || textAlign === "") {
            textAlign = "left";
          }
          newState[eventTypes.align.base] = textAlign;
        }

        setSelectedEventTypes((prev) => ({ ...prev, ...newState }));
      }
    });
  }, [editor]);

  const toggleStyle = (
    key: string,
    styleKey: string,
    value: string,
    check: (current: string) => boolean,
  ) => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const node = getSelectedNode(selection);
      const el =
        editor.getElementByKey(node.getKey()) ||
        editor.getElementByKey(node.getParent().getKey());

      const current = el
        ? window.getComputedStyle(el).getPropertyValue(styleKey)
        : "";

      const isActive = check(current);
      setSelectedEventTypes((prev: any) => ({ ...prev, [key]: !prev[key] }));

      $patchStyledNodes({
        [styleKey]: isActive ? "" : value,
      });
    });
  };

  useEffect(() => {
    updateToolbar();
  }, [editor, updateToolbar]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        LowPriority,
      ),
    );
  }, [editor, updateToolbar]);
  function onChange(type: string, event: unknown) {
    switch (type) {
      case eventTypes.bold:
        toggleStyle(eventTypes.bold, "font-weight", "700", (current) => {
          return (
            current === "bold" || (parseInt(current || "0", 10) || 0) >= 600
          );
        });
        break;
      case eventTypes.italic:
        toggleStyle(
          eventTypes.italic,
          "font-style",
          "italic",
          (current) => current === "italic",
        );
        break;
      case eventTypes.underline:
        toggleStyle(
          eventTypes.underline,
          "text-decoration",
          "underline",
          (current) => current.includes("underline"),
        );
        break;
      case eventTypes.align.base:
        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, event as any);
        break;
      case eventTypes.undo:
        editor.dispatchCommand(UNDO_COMMAND, undefined);
        break;
      case eventTypes.redo:
        editor.dispatchCommand(REDO_COMMAND, undefined);
        break;
      case eventTypes.listNumber:
        formatNumberedList();
        break;
      case eventTypes.listBullet:
        formatBulletList();
        break;
      case eventTypes.indent:
        addIndent();
        break;
      case eventTypes.indentDecrease:
        removeIndent();
        break;
      case eventTypes.color.base:
        applyFontColor(event as { background: string; text: string });
        break;
      case eventTypes.fontFamily: {
        const fontFamily = event as string;
        setSelectedEventTypes((prev: any) => ({
          ...prev,
          [eventTypes.fontFamily]: fontFamily,
        }));

        applyFontFamily(fontFamily);
        break;
      }
      case eventTypes.fontSize: {
        const fontSizeKey = event as string;
        const fontSizePx =
          OPTIONS.find((i) => i.key === fontSizeKey)?.sizePx ?? 16;
        setSelectedEventTypes((prev: any) => ({
          ...prev,
          [eventTypes.fontSize]: fontSizePx,
        }));
        applyFontSize(fontSizeKey);
        break;
      }
      case eventTypes.link:
        insertLink();
        break;

      default:
        break;
    }
  }

  const addIndent = () => {
    editor.dispatchCommand(INDENT_CONTENT_COMMAND, undefined);
  };

  const applyFontFamily = (fontFamily: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        $patchStyledNodes({ "font-family": fontFamily });
      }
    });
  };

  const applyFontSize = (fontSize: string) => {
    if (isSignatureActive) {
      const selectedFontSize =
        OPTIONS.find((i) => i.key === fontSize)?.htmlNodeVal ?? "2";
      // execCommand fontSize only accepts 1-7, use CSS instead:
      dispatchSignatureStyle(FORMAT_TO_EXEC.fontSize, selectedFontSize); // triggers styleWith accepted fontSize
    } else {
      const selectedFontSize =
        OPTIONS.find((i) => i.key === fontSize)?.sizePx ?? 16;
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyledNodes({ "font-size": `${selectedFontSize}px` });
        }
      });
    }
  };

  const applyFontColor = (colorObj: { background: string; text: string }) => {
    if (isSignatureActive) {
      dispatchSignatureStyle(FORMAT_TO_EXEC.backColor, colorObj.background); // background color
      dispatchSignatureStyle(FORMAT_TO_EXEC.foreColor, colorObj.text); // text color
    } else {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $patchStyledNodes({
            "background-color": colorObj.background,
            color: colorObj.text,
          });
        }
      });
    }
  };

  const insertLink = useCallback(() => {
    if (!isLink) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if (selection.isCollapsed()) {
            // Select the word to the left if selection is collapsed
            selection.modify("extend", true, "word");
          }
          const updatedSelection = $getSelection();
          if (
            $isRangeSelection(updatedSelection) &&
            !updatedSelection.isCollapsed()
          ) {
            editor.dispatchCommand(TOGGLE_LINK_COMMAND, "https://");
          }
        }
      });
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  }, [editor, isLink]);

  const removeIndent = () => {
    editor.dispatchCommand(OUTDENT_CONTENT_COMMAND, undefined);
  };

  const formatBulletList = () => {
    if (blockType !== "ul") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "ol") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };
  function getSelectedNode(selection: any) {
    const anchor = selection.anchor;
    const focus = selection.focus;
    const anchorNode = selection.anchor.getNode();
    const focusNode = selection.focus.getNode();
    if (anchorNode === focusNode) {
      return anchorNode;
    }
    const isBackward = selection.isBackward();
    if (isBackward) {
      return $isAtNodeEnd(focus) ? anchorNode : focusNode;
    } else {
      return $isAtNodeEnd(anchor) ? focusNode : anchorNode;
    }
  }

  return { onChange, isLink, editor, selectedEventTypes, blockType };
}

export default useOnClickListener;
